import { FunctionComponent, useEffect, useMemo, useState, useRef } from 'react';
import {
  ColDef,
  GridApi,
  GridOptions,
  ICellRendererParams,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
  GridReadyEvent,
  SortChangedEvent,
  GetContextMenuItemsParams,
} from 'ag-grid-community';
import { checkboxSelectionColumnDef } from '@/lib/ag-grid/selection';
import { LocationModel } from '@/modules/locations/types/LocationModel';
import LabelCellRenderer, { LabelCellRendererParams } from '@/components/grid/cells/LabelCellRenderer';
import { BASE_GRID_OPTIONS, PoGrid } from '@/components/grid/PoGrid';
import { useGridColumnState } from '@/hooks/useGridColumnState';
import { UserSettingKey } from '@/modules/users/api/user-settings/user-setting.contracts';
import { ColumnID } from '@/components/grid/column-ids';
import { PageSortOption, PageSortOrder } from '@/lib/api/pagination.page.dto';
import { LocationSortOption } from '@/modules/locations/api/locations/location.contracts';
import { useExportToExcel } from '@/hooks/useExportToExcel';
import { isNil, isEmpty } from 'lodash-es';
import dayjs from 'dayjs';
import { useTranslation } from '@/lib';
import { isBetaTenant } from '@/config/tenant-features';
import { useAuthUserContext } from '@/modules/auth/context/AuthUserContext';

interface IndividualLocationsGridProps {
  data: LocationModel[] | undefined;
  isLoading: boolean;
  onSelectionChanged: (selectedRows: LocationModel[]) => void;
  onRowDoubleClicked: (locationId: string) => void;
  hasPermission: boolean;
  initialSortOption?: PageSortOption;
  initialSortOrder?: PageSortOrder;
  onSortChanged: (sortOrder: SortChangedEvent<LocationModel>) => void;
}

const IndividualLocationsGrid: FunctionComponent<IndividualLocationsGridProps> = ({
  data,
  isLoading,
  onSelectionChanged,
  onRowDoubleClicked,
  hasPermission,
  initialSortOption,
  initialSortOrder,
  onSortChanged,
}) => {
  const { exportToExcel } = useExportToExcel();
  const [gridApi, setGridApi] = useState<GridApi<LocationModel>>();
  const { t } = useTranslation();
  const { tenantId } = useAuthUserContext();

  // Ref to hold the latest data value
  const dataRef = useRef<LocationModel[] | undefined>(data);

  // Update the ref whenever `data` changes
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Initialize the grid column state hook
  const {
    setColumnStateGridApi,
    handleColumnStateChange,
    columnState,
    applyStateToDefinitions,
    setIsAutoSaveEnabled,
    setDefaultColumnState,
  } = useGridColumnState(UserSettingKey.LOCATION_INDIVIDUAL_GRID_COLUMN_STATE);

  // Define the column definitions
  const columnDefs = useMemo<ColDef<LocationModel>[]>(() => {
    const columns: ColDef<LocationModel>[] = [
      {
        colId: ColumnID.LOCATION_NAME,
        field: 'dto.name',
        headerName: t('name'),
        sortable: true, // Enable sorting
      },
      {
        colId: ColumnID.LOCATION_CODE,
        field: 'dto.code',
        headerName: t('code'),
        width: 150,
        sortable: true, // Enable sorting
      },
    ];

    // Only add location type column for beta tenants
    if (isBetaTenant(tenantId)) {
      columns.push({
        colId: ColumnID.LOCATION_TYPE,
        field: 'dto.type',
        headerName: t('location_type'),
        width: 150,
        valueFormatter: (params) => {
          return params.data?.dto.type ? t(`location_type_${params.data.dto.type.toLowerCase()}`) : '-';
        },
        sortable: true, // Enable sorting
      });
    }

    columns.push({
      colId: ColumnID.LOCATION_LABELS,
      field: 'dto.labels',
      headerName: t('labels'),
      cellRenderer: LabelCellRenderer,
      cellRendererParams: (params: ICellRendererParams<LocationModel>): LabelCellRendererParams => {
        const labels = params.data?.dto.labels ?? [];
        return {
          labels,
        };
      },
      valueFormatter: (params) => {
        return params.data?.dto.labels?.map((label) => label.value.value).join(', ') ?? '-';
      },
      flex: 1,
      sortable: true, // Enable sorting
    });

    // Set default column state if not already set
    if (!columnState) {
      const defaultState = [
        { colId: ColumnID.LOCATION_NAME, hide: false },
        { colId: ColumnID.LOCATION_CODE, hide: false },
        { colId: ColumnID.LOCATION_LABELS, hide: false },
      ];

      // Only add location type to default state for beta tenants
      if (isBetaTenant(tenantId)) {
        defaultState.splice(3, 0, { colId: ColumnID.LOCATION_TYPE, hide: false });
      }

      setDefaultColumnState(defaultState);
    }

    // Apply saved column state to the column definitions
    applyStateToDefinitions(columns);

    return columns;
  }, [hasPermission, columnState]);

  // Enable auto-saving of column state
  useEffect(() => {
    setIsAutoSaveEnabled(true);
  }, []);

  // Export to Excel logic
  const onExportToExcelClicked = () => {
    if (!isNil(dataRef.current) && !isEmpty(dataRef.current)) {
      const exportData: Record<string, string | number | undefined>[] = dataRef.current.map((location) => {
        const data: Record<string, string | number | undefined> = {
          Code: location.dto.code,
          Name: location.dto.name,
        };

        // Only include Type column for beta tenants
        if (isBetaTenant(tenantId)) {
          data.Type = location.dto.type ? t(`location_type_${location.dto.type.toLowerCase()}`) : '-';
        }

        // Add labels
        const labels =
          location.dto.labels?.reduce<Record<string, string>>((acc, label, index) => {
            acc[`Label_${index + 1}`] = label.value.value;
            return acc;
          }, {}) ?? {};

        return { ...data, ...labels };
      });

      exportToExcel(exportData, `individual_locations_${dayjs().format('YYYY_MM_DD_HHmmss')}.xlsx`);
    }
  };

  // Handle sorting changes
  const handleSortChange = (event: SortChangedEvent<LocationModel>) => {
    handleColumnStateChange(event); // Handle the state change for columns

    // Call the onSortChanged callback to update the external state
    onSortChanged(event);

    // Apply the sort state to the grid
    const column = event.columns && event.columns[0];
    if (column) {
      gridApi?.applyColumnState({
        state: [
          {
            colId: column.getColId(),
            sort: column.getSort(),
          },
        ],
        defaultState: { sort: null },
      });
    }
  };

  // Define the grid options
  const gridOptions: GridOptions<LocationModel> = {
    ...BASE_GRID_OPTIONS,
    getContextMenuItems: (_: GetContextMenuItemsParams<LocationModel>) => [
      'copy',
      'separator',
      {
        name: 'Export to Excel',
        action: () => onExportToExcelClicked(),
      },
    ],
    onRowDoubleClicked(event: RowDoubleClickedEvent<LocationModel>) {
      const id = event.data?.dto.id.toString() ?? '';
      onRowDoubleClicked(id);
    },
    getRowId: (params) => params.data.dto.id.toString(),
    rowSelection: { mode: 'multiRow', checkboxes: hasPermission, headerCheckbox: hasPermission, enableClickSelection: true },
    selectionColumnDef: checkboxSelectionColumnDef(),
    onSelectionChanged: (event: SelectionChangedEvent<LocationModel>) => {
      onSelectionChanged(event.api.getSelectedRows());
    },
    onGridReady: (params: GridReadyEvent<LocationModel>) => {
      setGridApi(params.api);
      setColumnStateGridApi(params.api);

      // Apply initial sorting if provided
      if (initialSortOption && initialSortOrder) {
        let colId: ColumnID | undefined;

        switch (initialSortOption) {
          case LocationSortOption.NAME:
            colId = ColumnID.LOCATION_NAME;
            break;
          case LocationSortOption.CODE:
            colId = ColumnID.LOCATION_CODE;
            break;
          default:
            break;
        }

        if (colId) {
          params.api.applyColumnState({
            state: [
              {
                colId,
                sort: initialSortOrder === PageSortOrder.ASC ? 'asc' : 'desc',
              },
            ],
            defaultState: { sort: null },
          });
        }
      }
    },
    // Grid column state change handlers
    onColumnMoved: handleColumnStateChange,
    onColumnVisible: handleColumnStateChange,
    onColumnResized: handleColumnStateChange,
    onColumnPinned: handleColumnStateChange,
    onSortChanged: handleSortChange, // Add the sorting handler
  };

  return (
    <PoGrid
      isLoading={isLoading}
      colDefs={columnDefs}
      rowData={data}
      gridOptions={gridOptions}
      disableResizeColumnsToFit
      disableDefaultGridOptions
    />
  );
};

export default IndividualLocationsGrid;
